'use client';

import { useId, type ReactNode } from 'react';
import { LiquidSurface } from '@repo/ui/components/liquid-surface';
import { Skeleton } from '@repo/ui/components/skeleton';
import { cn } from '@repo/ui/lib/utils';

import { CopyAddressButton } from '@/components/copy-address-button';
import { formatSignedPercent } from '@/lib/format/percent/signed-percent';

/**
 * One line of a return attribution: something the return is split across
 * (a held vault on the investor portfolio, an asset on a vault page).
 *
 * `contributionBps` is `null` when the backend could not measure the line.
 * A null line is shown as unmeasured with its reason, never as 0%: its
 * share of the return sits in the residual, which is exactly what the
 * backend's reconciliation (lines + residual = total) says.
 */
interface ReturnAttributionLine {
  id: string;
  label: string;
  contributionBps: number | null;
  /** Plain-language qualifier under the label (held part of the window, or why unmeasured). */
  note?: string | null;
  /**
   * Set when the label is a truncated address (an asset with no registered
   * name): renders the copy control so the full address is one tap away.
   */
  address?: string;
}

export interface ReturnAttributionData {
  lines: readonly ReturnAttributionLine[];
  residualBps: number;
  totalBps: number;
}

interface ReturnAttributionProps {
  attribution?: ReturnAttributionData | null;
  isLoading?: boolean;
  isError?: boolean;
  /** Subtitle beside the heading. */
  summary: string;
  /** What the residual line holds, in the reader's words. */
  residualLabel: string;
  /** Optional one-line expansion under the residual label. */
  residualNote?: string;
  /** The reconciled total, e.g. "Window return" or "Vault return". */
  totalLabel: string;
  /** Shown when the whole attribution is unavailable (null). */
  unavailableMessage: string;
  errorMessage: string;
  /** Disclosure above the lines (e.g. how the weights were read). */
  notice?: ReactNode;
  /** Closing line under the totals. */
  footnote?: string;
}

function bps(value: number): string {
  return formatSignedPercent(value, { unit: 'bps', digits: 2, plus: true });
}

/**
 * Where a return came from: each line's contribution, served with an
 * explicit residual so the lines plus the residual equal the total exactly
 * (verified at each caller's parse boundary). The residual is disclosed as
 * its own line, never redistributed across the others.
 */
export function ReturnAttribution({
  attribution,
  isLoading = false,
  isError = false,
  summary,
  residualLabel,
  residualNote,
  totalLabel,
  unavailableMessage,
  errorMessage,
  notice,
  footnote
}: ReturnAttributionProps) {
  const titleId = useId();
  const lines = attribution?.lines ?? [];
  const maxAbs = lines.reduce(
    (max, line) =>
      line.contributionBps == null
        ? max
        : Math.max(max, Math.abs(line.contributionBps)),
    0
  );

  const body = (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={titleId} className="text-rowa-card text-rowa-text-primary">
          Where returns came from
        </h3>
        {/* Only a split on screen can add up to anything. */}
        {attribution && !isLoading ? (
          <p className="text-rowa-label-sm text-rowa-text-tertiary">
            {summary}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        // Shaped like the loaded panel (notice, lines, residual and total)
        // so a window change does not shift the page under the reader.
        <div aria-hidden>
          <Skeleton className="mt-3 h-10 w-full" />
          <div className="mt-4 space-y-2.5">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-6 w-full" />
            ))}
          </div>
          <div className="border-rowa-border-subtle mt-4 space-y-1.5 border-t pt-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-5 w-full" />
          </div>
        </div>
      ) : isError ? (
        <p className="text-rowa-body-sm text-rowa-text-secondary mt-4">
          {errorMessage}
        </p>
      ) : attribution == null ? (
        <p className="text-rowa-body-sm text-rowa-text-secondary mt-4">
          {unavailableMessage}
        </p>
      ) : (
        <>
          {notice ? (
            <div className="text-rowa-body-sm text-rowa-text-secondary mt-3">
              {notice}
            </div>
          ) : null}

          {lines.length > 0 ? (
            <ul className="mt-4 space-y-2.5">
              {lines.map((line) => {
                const value = line.contributionBps;
                const negative = value != null && value < 0;
                const widthPct =
                  value != null && maxAbs > 0
                    ? Math.max(
                        (Math.abs(value) / maxAbs) * 100,
                        value === 0 ? 0 : 2
                      )
                    : 0;
                return (
                  <li
                    key={line.id}
                    className="grid grid-cols-[minmax(7rem,14rem)_minmax(0,1fr)_5.5rem] items-center gap-3"
                  >
                    <span className="min-w-0">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="text-rowa-body-sm text-rowa-text-primary truncate">
                          {line.label}
                        </span>
                        {line.address ? (
                          <CopyAddressButton address={line.address} />
                        ) : null}
                      </span>
                      {line.note ? (
                        <span className="text-rowa-label-sm text-rowa-text-tertiary block">
                          {line.note}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className="bg-rowa-surface-inset h-1.5 overflow-hidden rounded-full"
                      aria-hidden
                    >
                      {value != null ? (
                        <span
                          className={cn(
                            'block h-full rounded-full',
                            negative && 'bg-rowa-status-error'
                          )}
                          style={{
                            width: `${widthPct}%`,
                            ...(negative
                              ? {}
                              : {
                                  backgroundColor: 'var(--color-rowa-chart-2)'
                                })
                          }}
                        />
                      ) : null}
                    </span>
                    <span
                      className={cn(
                        'text-rowa-body-sm text-right font-mono tabular-nums',
                        value == null
                          ? 'text-rowa-text-tertiary'
                          : negative
                            ? 'text-rowa-status-error'
                            : 'text-rowa-text-primary'
                      )}
                    >
                      {value == null ? (
                        <>
                          <span aria-hidden>—</span>
                          <span className="sr-only">Not measured</span>
                        </>
                      ) : (
                        bps(value)
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}

          <dl className="border-rowa-border-subtle mt-4 space-y-1.5 border-t pt-3">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-rowa-label-sm text-rowa-text-tertiary">
                {residualLabel}
                {residualNote ? (
                  <span className="text-rowa-label-sm text-rowa-text-tertiary block">
                    {residualNote}
                  </span>
                ) : null}
              </dt>
              <dd className="text-rowa-label-sm text-rowa-text-secondary text-right font-mono tabular-nums">
                {bps(attribution.residualBps)}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-rowa-body-sm text-rowa-text-primary font-medium">
                {totalLabel}
              </dt>
              <dd className="text-rowa-body-sm text-rowa-text-primary text-right font-mono font-medium tabular-nums">
                {bps(attribution.totalBps)}
              </dd>
            </div>
          </dl>

          {footnote ? (
            <p className="text-rowa-label-sm text-rowa-text-tertiary mt-3">
              {footnote}
            </p>
          ) : null}
        </>
      )}
    </>
  );

  return (
    <LiquidSurface
      type="panel"
      role="region"
      aria-labelledby={titleId}
      className="p-6"
    >
      {body}
    </LiquidSurface>
  );
}
